/**
 * Recurring appointment types.
 *
 * Recurring appointments are for BLOCKED TIME ONLY — lunch breaks, admin blocks, theatre
 * lists. The backend rejects a repeat on any patient appointment type, on any appointment
 * carrying a patientId, and on any appointment carrying booking metadata. The gate is the
 * TYPE, not a null patientId: notification recipients fall back to the metadata, so a
 * patient-type appointment with a cell number but no patient would still send SMS. Blocking
 * on the type is what makes a series provably notification-free.
 *
 * Mirror that gate in the UI with `canRepeatAppointmentType` rather than re-deriving it.
 */

export type RecurrenceFrequency = 'Daily' | 'Weekly' | 'Monthly';

export type RecurrenceWeekday =
  'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

export type RecurrenceNthWeek = 'First' | 'Second' | 'Third' | 'Fourth' | 'Last';

/**
 * How far an edit or a cancel reaches.
 *
 * There is deliberately no 'All' — the backend's position is that All is exactly
 * ThisAndFollowing anchored at the first occurrence, so "I created this whole series by
 * mistake" is served by targeting the earliest one. Sending an unrecognised value is
 * rejected outright rather than narrowed to 'This', so never invent a third option.
 */
export type SeriesScope = 'This' | 'ThisAndFollowing';

/** Server limits (RecurrenceBounds). Mirrored so the form can stop a request that cannot succeed. */
export const RECURRENCE_HORIZON_DAYS = 365;
export const RECURRENCE_MAX_OCCURRENCES = 365;
export const RECURRENCE_MAX_INTERVAL = 365;

/**
 * The largest "every N units" each frequency allows, mirroring `RecurrenceBounds.MaxIntervalFor`.
 *
 * `RECURRENCE_MAX_INTERVAL` is the horizon in days — the value that holds for the densest
 * frequency. At weekly and monthly it leaves a wide band that passes validation and then
 * cannot produce a second occurrence, so "every 300 months" was accepted and then refused by
 * a message about occurrence counts that never mentioned the interval.
 *
 * Each value is the largest interval whose second occurrence still lands on or inside the
 * horizon: 365 days, 52 weeks (364 days), 12 months.
 *
 * The flat constant stays for the server's own arithmetic ceiling; these only tighten it.
 *
 * Monthly 12 is not exact, deliberately. The horizon is 365 days but a year is not always 365
 * days, so a twelve-month span containing 29 February overshoots it by one — a series starting
 * 2024-01-01 has its horizon at 2024-12-31 while twelve months on is 2025-01-01. Those starts
 * pass this check and are then caught by the horizon projection instead, which walks from the
 * real start date. That is the intended division: these limits exist to give a better message,
 * and the projection is what decides. Lowering monthly to 11 would make the bound
 * unconditional at the cost of refusing every annual repeat, including the ones that fit.
 *
 * Weekly 52 is conservative for the same reason, in the other direction. The server anchors
 * weekly blocks on the Sunday of the start's week, so a Saturday start with Sunday among the
 * selected days does fit a second occurrence at an interval of 53 — the next block's Sunday
 * lands exactly on the horizon. This bound rejects that schedule. Deliberate: answering
 * correctly needs the start date and the chosen weekdays, and the case needs all three of a
 * Saturday start, Sunday selected, and an interval of 53.
 *
 * The two are not symmetrical in cost. A loose bound spends a less specific error message; a
 * tight one refuses a booking the practice is entitled to make, with no way around it here.
 * If either is revisited, that is the reason to revisit this one first.
 */
export const RECURRENCE_MAX_INTERVAL_BY_FREQUENCY: Record<RecurrenceFrequency, number> = {
  Daily: RECURRENCE_HORIZON_DAYS,
  Weekly: Math.floor(RECURRENCE_HORIZON_DAYS / 7), // 52
  Monthly: 12,
};

/** The unit an interval counts, for labels and messages: "every N days / weeks / months". */
export const RECURRENCE_INTERVAL_UNIT: Record<RecurrenceFrequency, string> = {
  Daily: 'days',
  Weekly: 'weeks',
  Monthly: 'months',
};

/**
 * The largest interval this frequency allows. Falls back to the flat ceiling for a frequency
 * the map does not cover, so an unrecognised value cannot silently become unbounded.
 */
export function maxIntervalFor(frequency: RecurrenceFrequency): number {
  return RECURRENCE_MAX_INTERVAL_BY_FREQUENCY[frequency] ?? RECURRENCE_MAX_INTERVAL;
}

export const RECURRENCE_WEEKDAYS: RecurrenceWeekday[] =
  ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const RECURRENCE_NTH_WEEKS: RecurrenceNthWeek[] =
  ['First', 'Second', 'Third', 'Fourth', 'Last'];

/**
 * The repeat block sent on create. Enum-ish fields are the server's enum NAMES —
 * "Weekly", "Friday", "Last". Numeric strings are explicitly rejected server-side
 * ("0" must not mean Daily), so never send an ordinal.
 *
 * Two pairs are mutually exclusive and the server rejects both-or-neither:
 *   - monthlyDayOfMonth  XOR  (monthlyNth + monthlyWeekday)
 *   - endsAfterCount     XOR  endsOnDate   (exactly one is required)
 * The recurrence form prevents these structurally with radio groups rather than
 * validating after the fact.
 */
export interface RecurrenceRequest {
  frequency: RecurrenceFrequency;
  /** Every N days/weeks/months. 1..365. */
  interval: number;
  /** Weekly only. Empty means "the same weekday the appointment starts on". */
  weeklyDays?: RecurrenceWeekday[];
  /** Monthly only, "on day N". */
  monthlyDayOfMonth?: number;
  /** Monthly only, "on the [First..Last] [weekday]". Set together with monthlyWeekday. */
  monthlyNth?: RecurrenceNthWeek;
  monthlyWeekday?: RecurrenceWeekday;
  /** Stop after N occurrences produced. 1..365. */
  endsAfterCount?: number;
  /**
   * Stop once occurrences pass this date, inclusive. The server compares only the DATE
   * part, so this must carry the calendar date the user actually picked.
   *
   * Zone-less wall-clock on the wire — build it with `pickerToWallClock`, exactly as
   * appointment start/end are built, and never assign a raw picker `Date`. A `Date` is
   * serialised by JSON.stringify as a UTC instant, so from any positive-offset zone
   * (SAST included) a local midnight goes out as the previous day at 22:00 and the series
   * silently loses its final occurrence. Typed `Date` to match the other wire fields,
   * which are likewise strings at runtime.
   */
  endsOnDate?: Date;
}

/**
 * The rule as read back on an occurrence. Same shape as the request, plus the
 * server-derived monthlyMode.
 *
 * Normalised by `normalizeRecurrenceRule` — do not consume the raw wire value, see there.
 */
export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  weeklyDays: RecurrenceWeekday[];
  monthlyDayOfMonth?: number;
  monthlyNth?: RecurrenceNthWeek;
  monthlyWeekday?: RecurrenceWeekday;
  endsAfterCount?: number;
  endsOnDate?: Date;
  /** Which monthly branch is live. Derived server-side; never sent on a request. */
  monthlyMode: 'None' | 'ByDayOfMonth' | 'ByNthWeekday';
}

const FREQUENCIES: RecurrenceFrequency[] = ['Daily', 'Weekly', 'Monthly'];
const MONTHLY_MODES: RecurrenceRule['monthlyMode'][] = ['None', 'ByDayOfMonth', 'ByNthWeekday'];

/**
 * Read one enum-ish field off the wire, accepting either the name or the ordinal.
 *
 * Both are accepted on purpose. The write contract is names, but the read path currently
 * emits ORDINALS: RecurrenceRule's string-enum attributes on the server are Newtonsoft
 * ones (they drive the jsonb persistence path) and the API serialises responses with
 * System.Text.Json, where they are inert. That asymmetry looks unintended, so this reads
 * both — the day the backend fixes it, nothing here changes.
 *
 * Anything unrecognised returns undefined rather than defaulting to the first member: a
 * blank "repeats" summary is a visible bug, silently claiming "Daily" is not.
 */
function readEnum<T extends string>(value: unknown, names: T[]): T | undefined {
  if (typeof value === 'number') {
    return names[value];
  }
  if (typeof value === 'string') {
    // A numeric string is still an ordinal, e.g. "1" — some serialisers quote enum numbers.
    if (/^\d+$/.test(value)) {
      return names[Number(value)];
    }
    return names.find(name => name.toLowerCase() === value.toLowerCase());
  }
  return undefined;
}

function readDate(value: unknown): Date | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value as string);
  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Turn the raw `recurrenceRule` off an appointment response into the normalised shape the
 * app uses. Returns undefined for a non-recurring appointment, and for a rule whose
 * frequency can't be read at all — a rule we can't describe is worse than no rule, because
 * every consumer would render a confidently wrong summary.
 */
export function normalizeRecurrenceRule(raw: unknown): RecurrenceRule | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const source = raw as Record<string, unknown>;

  const frequency = readEnum(source['frequency'], FREQUENCIES);
  if (!frequency) {
    return undefined;
  }

  const weeklyDays = Array.isArray(source['weeklyDays'])
    ? (source['weeklyDays'] as unknown[])
        .map(day => readEnum(day, RECURRENCE_WEEKDAYS))
        .filter((day): day is RecurrenceWeekday => !!day)
    : [];

  return {
    frequency,
    interval: typeof source['interval'] === 'number' ? source['interval'] : 1,
    weeklyDays,
    monthlyDayOfMonth: typeof source['monthlyDayOfMonth'] === 'number' ? source['monthlyDayOfMonth'] : undefined,
    monthlyNth: readEnum(source['monthlyNth'], RECURRENCE_NTH_WEEKS),
    monthlyWeekday: readEnum(source['monthlyWeekday'], RECURRENCE_WEEKDAYS),
    endsAfterCount: typeof source['endsAfterCount'] === 'number' ? source['endsAfterCount'] : undefined,
    endsOnDate: readDate(source['endsOnDate']),
    monthlyMode: readEnum(source['monthlyMode'], MONTHLY_MODES) ?? 'None',
  };
}

/**
 * Whether a repeat can be set up on this appointment type. Blocked time only — see the
 * note at the top of this file. `canCreate` is included because the server's
 * AllowsNotifications gate is `IsPatientAppointment && CanCreate`; a type the user can't
 * book at all should not offer a repeat either.
 */
export function canRepeatAppointmentType(type: {isPatientAppointment?: boolean; canCreate?: boolean} | null): boolean {
  if (!type) {
    return false;
  }
  return !type.isPatientAppointment && type.canCreate !== false;
}
